import React from 'react';
import { useMeeting } from '../../context/MeetingContext';
import { useAuth } from '../../context/AuthContext';
import { Video, Mic, MicOff, Camera, CameraOff, PhoneOff, Monitor, Copy, Check, Users } from 'lucide-react';

export const MeetingCallGrid: React.FC = () => {
  const { user } = useAuth();
  const {
    activeMeetingRoom,
    participants,
    localStream,
    remoteStreams,
    isJoined,
    isMuted,
    isCameraOff,
    isScreenSharing,
    currentUserId,
    leaveMeetingRoom,
    endMeetingRoom,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
  } = useMeeting();

  const [copiedLink, setCopiedLink] = React.useState(false);

  if (!isJoined || !activeMeetingRoom) return null;

  const isHost = activeMeetingRoom.hostId === user?.id || activeMeetingRoom.hostId === currentUserId;
  const uniqueParticipants = Array.from(new Map(participants.map((p) => [p.userId, p])).values());

  const handleCopyLink = () => {
    const link = `${window.location.origin}/meet/${activeMeetingRoom.meetingId}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-md flex flex-col justify-between p-4 overflow-hidden">
      {/* Top Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-card/60 border border-border/50 rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-xl">
            <Video className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-foreground">{activeMeetingRoom.title}</h2>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3 text-primary" /> {uniqueParticipants.length} In Meeting
              </span>
              <span>•</span>
              <span className="text-emerald-400 font-semibold">{activeMeetingRoom.status}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopyLink}
            className="px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-lg transition flex items-center gap-1.5 font-medium"
          >
            {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            {copiedLink ? 'Copied' : 'Copy Link'}
          </button>
        </div>
      </div>

      {/* Video Grid Area */}
      <div className="flex-1 my-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-center justify-center overflow-y-auto">
        {/* Local Participant Tile */}
        <div className="relative aspect-video bg-muted/30 border border-border rounded-2xl overflow-hidden shadow-lg flex items-center justify-center">
          {localStream && !isCameraOff ? (
            <video
              ref={(videoEl) => {
                if (videoEl && localStream) videoEl.srcObject = localStream;
              }}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <div className="w-16 h-16 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-xl">
                {user?.displayName?.[0] || 'Me'}
              </div>
              <span className="text-xs font-semibold">{user?.displayName || 'You'}</span>
            </div>
          )}
          <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-lg text-white text-[11px] font-medium flex items-center gap-1.5">
            <span>You</span>
            {isMuted && <MicOff className="w-3 h-3 text-red-400" />}
          </div>
        </div>

        {/* Remote Participants */}
        {uniqueParticipants
          .filter((p) => p.userId !== currentUserId && p.userId !== user?.id)
          .map((p) => {
            const remoteStream = remoteStreams.get(p.userId);
            return (
              <div key={p.userId} className="relative aspect-video bg-muted/30 border border-border rounded-2xl overflow-hidden shadow-lg flex items-center justify-center">
                {remoteStream && !p.videoOff ? (
                  <video
                    ref={(videoEl) => {
                      if (videoEl && remoteStream) videoEl.srcObject = remoteStream;
                    }}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <div className="w-16 h-16 rounded-full bg-muted text-foreground flex items-center justify-center font-bold text-xl border border-border">
                      {p.displayName?.[0] || 'U'}
                    </div>
                    <span className="text-xs font-semibold">{p.displayName}</span>
                  </div>
                )}
                <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-lg text-white text-[11px] font-medium flex items-center gap-1.5">
                  <span>{p.displayName}</span>
                  {p.audioMuted && <MicOff className="w-3 h-3 text-red-400" />}
                </div>
              </div>
            );
          })}
      </div>

      {/* Bottom Floating Control Bar */}
      <div className="w-fit mx-auto px-6 py-3 bg-card border border-border rounded-full shadow-2xl flex items-center gap-4">
        <button
          type="button"
          onClick={toggleMute}
          className={`p-3 rounded-full transition ${
            isMuted ? 'bg-destructive text-destructive-foreground' : 'bg-muted hover:bg-muted/80 text-foreground'
          }`}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>

        <button
          type="button"
          onClick={toggleCamera}
          className={`p-3 rounded-full transition ${
            isCameraOff ? 'bg-destructive text-destructive-foreground' : 'bg-muted hover:bg-muted/80 text-foreground'
          }`}
          title={isCameraOff ? 'Turn Camera On' : 'Turn Camera Off'}
        >
          {isCameraOff ? <CameraOff className="w-5 h-5" /> : <Camera className="w-5 h-5" />}
        </button>

        <button
          type="button"
          onClick={toggleScreenShare}
          className={`p-3 rounded-full transition ${
            isScreenSharing ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-foreground'
          }`}
          title="Share Screen"
        >
          <Monitor className="w-5 h-5" />
        </button>

        <div className="w-px h-6 bg-border mx-1" />

        <button
          type="button"
          onClick={leaveMeetingRoom}
          className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-full transition flex items-center gap-2 shadow-md"
        >
          <PhoneOff className="w-4 h-4" /> Leave
        </button>

        {isHost && (
          <button
            type="button"
            onClick={endMeetingRoom}
            className="px-4 py-2.5 bg-destructive hover:bg-destructive/90 text-white text-xs font-bold rounded-full transition shadow-md"
          >
            End Meeting
          </button>
        )}
      </div>
    </div>
  );
};
